/**
 * TakeEngine — takes, trechos, punch-in/out e comping.
 *
 * Princípio: o take gravado NUNCA é destruído por uma regravação. Cada take é
 * um conjunto de segmentos com posição na linha do tempo. O "buffer da faixa"
 * é montado a partir do take ativo (ou do mapa de comping).
 */
import type { CompingRegion, RecordedSegment, Take, Track } from '../types.js';
import { applyComping, mergeSegments, punchReplace, resampleLinear, takeDuration } from './buffers.js';
import { peak } from './analysis.js';
import { sliceData } from './buffers.js';
import { uid } from '../utils/misc.js';

export interface BuildOptions {
  sampleRate: number;
  start?: number;
  duration?: number;
  overlap?: 'last-wins' | 'crossfade';
  crossfadeMs?: number;
}

export class TakeEngine {
  /** Cria um take a partir de um resultado de gravação. */
  createTake(opts: {
    trackId: string;
    index: number;
    segments: RecordedSegment[];
    mediaStart: number;
    diag?: Take['diag'];
  }): Take {
    let p = 0;
    let clipped = false;
    for (const s of opts.segments) {
      p = Math.max(p, s.peak);
      clipped = clipped || s.clipped;
    }
    return {
      id: uid('take'),
      index: opts.index,
      trackId: opts.trackId,
      createdAt: Date.now(),
      name: `Take ${opts.index}`,
      segments: opts.segments,
      recordedSeconds: opts.segments.reduce((s, seg) => s + seg.samples.length / seg.sampleRate, 0),
      mediaStart: opts.mediaStart,
      peak: p,
      clipped,
      diag: opts.diag,
    };
  }

  /** Take -> Float32Array contínuo (com posições preservadas). */
  takeToFloat(take: Take, opts: BuildOptions): { data: Float32Array; offset: number; duration: number } {
    return mergeSegments(take.segments, opts);
  }

  /** Duração real gravada do take. */
  duration(take: Take): number {
    return takeDuration(take);
  }

  /** Monta o buffer da faixa: take ativo + regiões de comping (se houver). */
  buildTrackAudio(track: Track, takes: Take[], opts: BuildOptions): {
    data: Float32Array;
    offset: number;
    duration: number;
    mode: 'empty' | 'take' | 'comping';
  } {
    if (track.comping && track.regionMap?.length) {
      const relevant = takes.filter((t) => t.trackId === track.id);
      if (relevant.length) {
        const r = applyComping(relevant, track.regionMap, opts);
        if (r.data.length) return { ...r, mode: 'comping' };
      }
    }
    const active = takes.find((t) => t.id === track.activeTakeId) ??
      [...takes].filter((t) => t.trackId === track.id).sort((a, b) => b.index - a.index)[0];
    if (!active) return { data: new Float32Array(0), offset: 0, duration: 0, mode: 'empty' };
    const r = this.takeToFloat(active, opts);
    return { ...r, mode: 'take' };
  }

  /**
   * Punch-in: substitui apenas o trecho [start,end] da faixa montada pelo
   * trecho equivalente do take novo. O resto é preservado byte a byte.
   */
  punchInto(
    base: Float32Array,
    newTake: Take,
    region: { start: number; end: number },
    sampleRate: number,
    crossfadeMs = 15
  ): Float32Array {
    const merged = mergeSegments(newTake.segments, { sampleRate, start: 0, duration: region.end + 1 });
    const patch = sliceData(merged.data, sampleRate, region.start, region.end);
    return punchReplace(base, patch, sampleRate, region.start, crossfadeMs);
  }

  /** Cria/atualiza regiões de comping sem sobreposição. */
  setRegion(map: CompingRegion[], region: CompingRegion): CompingRegion[] {
    const out = map
      .filter((r) => r.id !== region.id)
      .map((r) => {
        // recorta regiões existentes que colidem com a nova
        if (r.end <= region.start || r.start >= region.end) return r;
        const pieces: CompingRegion[] = [];
        if (r.start < region.start) pieces.push({ ...r, end: region.start });
        if (r.end > region.end) pieces.push({ ...r, id: uid('region'), start: region.end });
        return pieces;
      })
      .flat();
    out.push(region);
    return out.sort((a, b) => a.start - b.start);
  }

  removeRegion(map: CompingRegion[], id: string): CompingRegion[] {
    return map.filter((r) => r.id !== id);
  }

  /** Auto-região: divide a música em N partes iguais para começar um comping. */
  autoRegions(takes: Take[], duration: number, parts = 4): CompingRegion[] {
    if (!takes.length || duration <= 0) return [];
    const step = duration / parts;
    const out: CompingRegion[] = [];
    for (let i = 0; i < parts; i++) {
      const take = takes[i % takes.length];
      out.push({
        id: uid('region'),
        start: i * step,
        end: (i + 1) * step,
        takeId: take.id,
        label: `Parte ${i + 1}`,
      });
    }
    return out;
  }

  /** Escolhe o melhor take automaticamente (pico saudável + sem clipping). */
  pickBestTake(takes: Take[]): Take | null {
    if (!takes.length) return null;
    const scored = takes.map((t) => {
      const p = t.peak;
      const db = 20 * Math.log10(Math.max(1e-9, p));
      // alvo: pico entre -9 e -1.5 dBFS
      const target = db > -1.5 ? (db + 1.5) * 2 : db < -9 ? (-9 - db) * 1.2 : 0;
      const dur = takeDuration(t);
      return { t, score: target + (t.clipped ? 50 : 0) + (dur < 1 ? 30 : 0) };
    });
    scored.sort((a, b) => a.score - b.score);
    return scored[0].t;
  }

  /** Compara dois takes num trecho (A/B) devolvendo níveis. */
  compareTakes(a: Take, b: Take, region: { start: number; end: number }, sampleRate: number) {
    const A = sliceData(mergeSegments(a.segments, { sampleRate }).data, sampleRate, region.start, region.end);
    const B = sliceData(mergeSegments(b.segments, { sampleRate }).data, sampleRate, region.start, region.end);
    return {
      a: { peak: peak(A), seconds: A.length / sampleRate },
      b: { peak: peak(B), seconds: B.length / sampleRate },
    };
  }

  /** Normaliza sample rates diferentes (Bluetooth costuma vir em 16/24 kHz). */
  normalizeSampleRate(segments: RecordedSegment[], targetRate: number): RecordedSegment[] {
    return segments.map((s) =>
      s.sampleRate === targetRate
        ? s
        : { ...s, samples: resampleLinear(s.samples, s.sampleRate, targetRate), sampleRate: targetRate }
    );
  }
}

export const takeEngine = new TakeEngine();
